import * as React from 'react';
import { StyleSheet, View, Text, Image, TouchableOpacity, Switch } from 'react-native';
import { useLocation } from 'react-router';
import { useQuery } from '@apollo/react-hooks';
import { font } from '../../style/text';
import color from '../../style/color';
import { getLocalizedTexts } from '../../Locales';
import { logEventWithoutAuthentication } from '../../api/rest/events';
import type { Component } from '@checkout.com/checkout-web-components';
import { WalletType, CheckoutMethodType, useLoadCheckoutFlowComponent } from './useGiftCardCheckout';
import { useCyclingText, LottieWithText, getPrePaymentTexts } from './giftCardPaymentUtils';
import { OrderData, formatOrderData } from './giftCardPaymentModel';
import {
    WalletMethodRow,
    GranularConditionsBlock,
    GiftCardPreview,
    OrderSummary,
    PAYMENT_METHOD_LOGOS,
} from './GiftCardPaymentComponents';
import {
    giftCardPaymentSessionOrderDataQuery,
    GiftCardPaymentSessionOrderDataQueryResponse,
    GiftCardPaymentSessionOrderDataQueryVariables,
} from '../../api/graphql/queries/giftCardPaymentSessionOrderData';

const logo = { uri: '/assets/images/logos/logo-with-title.svg' };
const chevronLeft = { uri: '/assets/images/icons/chevron-left.svg' };

/**
 * This page is opened from the browser extension to render the checkout.com Flow payment component.
 *
 * URL parameters:
 * - paymentSessionToken: The checkout.com payment session token for Flow initialization
 * - paymentSessionId: The Joko payment session ID for tracking
 * - platformIssuerId: The platform issuer ID for the gift card
 * - numberOfCards: Number of gift cards in the basket (for singular/plural text)
 * - showTermsAndConditions: 'true' | 'false' — whether to show T&C checkbox (default: false)
 *
 * Order display data is fetched from the backend via `giftCardPaymentSessionOrderData` query.
 *
 * After payment completion, Flow will redirect to the successUrl or failureUrl.
 * The extension's background script detects this redirect and sends a message to the contentscript.
 */
function GiftCardPaymentPage() {
    const location = useLocation();
    const urlSearchParams = new URLSearchParams(location.search);
    const paymentSessionToken = urlSearchParams.get('paymentSessionToken');
    const paymentSessionId = urlSearchParams.get('paymentSessionId');
    const platformIssuerId = urlSearchParams.get('platformIssuerId');
    const numberOfCards = parseInt(urlSearchParams.get('numberOfCards') ?? '1', 10) || 1;
    const showTermsAndConditions = urlSearchParams.get('showTermsAndConditions') === 'true';
    const orderDataQuery = useQuery<
        GiftCardPaymentSessionOrderDataQueryResponse,
        GiftCardPaymentSessionOrderDataQueryVariables
    >(giftCardPaymentSessionOrderDataQuery, {
        variables: { paymentSessionId: paymentSessionId ?? '' },
        skip: !paymentSessionId,
    });
    const orderData: OrderData | null = React.useMemo(
        () => formatOrderData(orderDataQuery.data?.giftCardPaymentSessionOrderData),
        [orderDataQuery.data]
    );
    // null until checkout.com isAvailable() probing completes after the SDK is initialized
    const [availableWalletMethods, setAvailableWalletMethods] = React.useState<Set<WalletType> | null>(null);
    // null until availability probing completes; then auto-set to best available method
    const [selectedMethod, setSelectedMethod] = React.useState<CheckoutMethodType | null>(null);
    const sharedParams = new URLSearchParams({
        paymentSessionId: paymentSessionId ?? '',
        platformIssuerId: platformIssuerId ?? '',
    });
    const failureUrl = `/gift-card-payment/failure?${sharedParams}`;
    const successUrl = `/gift-card-payment/success?${new URLSearchParams({
        ...Object.fromEntries(sharedParams),
        numberOfCards: String(numberOfCards),
        paymentMethodType: selectedMethod ?? 'payment_card',
    })}`;
    const [status, setStatus] = React.useState<'loading' | 'ready' | 'error'>('loading');
    const [isSavedForFuturePurchases, setIsSavedForFuturePurchases] = React.useState(false);
    const [termsAccepted, setTermsAccepted] = React.useState(false);
    const checkoutComponentRef = React.useRef<Component | null>(null);
    const handleWalletAvailabilityProbed = React.useCallback((methods: Set<WalletType>) => {
        setAvailableWalletMethods(methods);
        setSelectedMethod((current) => {
            if (current !== null) return current;
            if (methods.has('apple_pay')) return 'apple_pay';
            if (methods.has('google_pay')) return 'google_pay';
            return 'payment_card';
        });
    }, []);
    useLoadCheckoutFlowComponent(
        paymentSessionToken,
        setStatus,
        successUrl,
        failureUrl,
        orderDataQuery.error ? null : selectedMethod,
        checkoutComponentRef,
        handleWalletAvailabilityProbed
    );
    const texts = getLocalizedTexts().giftCardPaymentPage;
    const prePaymentTexts = React.useMemo(() => getPrePaymentTexts(numberOfCards), [numberOfCards]);
    const loadingText = useCyclingText(prePaymentTexts);

    // Auto-redirect to failure URL when Checkout.com form fails to load
    React.useEffect(() => {
        if (status !== 'error') return;
        logEventWithoutAuthentication(undefined, {
            type: 'checkoutDotComPaymentFailed',
            payload: { paymentSessionId, platformIssuerId },
        });
        const timer = setTimeout(() => {
            window.location.href = failureUrl;
        }, 5000);
        return () => clearTimeout(timer);
    }, [status, failureUrl]);

    const handleBack = React.useCallback(() => {
        window.location.href = `${failureUrl}&reason=cancelled`;
    }, [failureUrl]);

    const handleSelectMethod = React.useCallback(
        (method: CheckoutMethodType) => {
            if (method === selectedMethod) return;
            setSelectedMethod(method);
            setTermsAccepted(false);
        },
        [selectedMethod]
    );

    const handleToggleSaveCard = React.useCallback(
        (value: boolean) => {
            setIsSavedForFuturePurchases(value);
            window.postMessage(
                { type: 'joko:giftCardSavePreference', shouldSave: value, paymentSessionId },
                window.location.origin
            );
        },
        [paymentSessionId]
    );

    const showSaveCardToggle = selectedMethod === 'payment_card' && status === 'ready';
    const canPay = !showTermsAndConditions || termsAccepted;

    if (orderDataQuery.error || (!orderDataQuery.loading && !orderData))
        return (
            <View style={styles.page}>
                <View style={styles.header}>
                    <Image source={logo} style={styles.headerLogo} />
                </View>
                <View style={[styles.content, { justifyContent: 'center', alignItems: 'center' }]}>
                    <Text style={styles.pageHeading}>{texts.paymentError}</Text>
                    <Text style={styles.pageSubtitle}>{texts.orderDataErrorSubtitle}</Text>
                </View>
            </View>
        );

    return (
        <View style={styles.page}>
            <View style={styles.header}>
                <Image source={logo} style={styles.headerLogo} />
            </View>

            <View style={styles.content}>
                <TouchableOpacity style={styles.backLink} onPress={handleBack}>
                    <Image source={chevronLeft} style={styles.backIcon} />
                    <Text style={styles.backText}>{texts.back}</Text>
                </TouchableOpacity>

                <View style={styles.body}>
                    <View style={styles.leftColumn}>
                        {status === 'loading' && <LottieWithText text={loadingText} />}
                        {status === 'error' && (
                            <View style={styles.messageContainer}>
                                <Text style={styles.pageHeading}>{texts.paymentError}</Text>
                                <Text style={styles.pageSubtitle}>{texts.paymentFormErrorSubtitle}</Text>
                            </View>
                        )}
                        {status === 'ready' && (
                            <View style={styles.messageContainer}>
                                <Text style={styles.pageHeading}>{texts.secureCheckoutTitle}</Text>
                                <Text style={styles.pageSubtitle}>{texts.secureCheckoutSubtitle}</Text>
                            </View>
                        )}

                        {/* Payment method picker — shown when ready; wallet rows filtered by checkout.com isAvailable() */}
                        {status === 'ready' && (
                            <View style={styles.walletMethodsBox}>
                                {availableWalletMethods?.has('apple_pay') && (
                                    <WalletMethodRow
                                        logo={PAYMENT_METHOD_LOGOS.applePayLogo}
                                        label="Apple Pay"
                                        selected={selectedMethod === 'apple_pay'}
                                        onPress={() => handleSelectMethod('apple_pay')}
                                        hasBorderBottom={true}
                                    />
                                )}
                                {availableWalletMethods?.has('google_pay') && (
                                    <WalletMethodRow
                                        logo={PAYMENT_METHOD_LOGOS.googlePayLogo}
                                        label="Google Pay"
                                        selected={selectedMethod === 'google_pay'}
                                        onPress={() => handleSelectMethod('google_pay')}
                                        hasBorderBottom={true}
                                    />
                                )}
                                <WalletMethodRow
                                    logo={PAYMENT_METHOD_LOGOS.creditCardLogo}
                                    label={texts.creditCard}
                                    selected={selectedMethod === 'payment_card'}
                                    onPress={() => handleSelectMethod('payment_card')}
                                    hasBorderBottom={false}
                                />
                            </View>
                        )}

                        {/* Card form box — always in DOM (opacity:0 when inactive) so the SDK can initialize;
                         * wallet methods use a separate container below the checkbox instead. */}
                        <View
                            style={[
                                styles.cardFormBox,
                                selectedMethod !== 'payment_card' ? styles.cardFormBoxHidden : undefined,
                                status !== 'ready' ? styles.cardFormBoxHidden : undefined,
                            ]}>
                            <div id="checkout-card-container" />
                            {showSaveCardToggle && (
                                <View style={styles.saveCardRow}>
                                    <Text style={styles.saveCardLabel}>{texts.saveForNextPurchases}</Text>
                                    <Switch
                                        value={isSavedForFuturePurchases}
                                        onValueChange={handleToggleSaveCard}
                                        trackColor={{ false: '#E3E3E3', true: '#64e76a' }}
                                        thumbColor="#FFFFFF"
                                        ios_backgroundColor="#E3E3E3"
                                    />
                                </View>
                            )}
                        </View>

                        {/* Terms and pay button */}
                        <View
                            style={status !== 'ready' ? { opacity: 0 } : undefined}
                            pointerEvents={status !== 'ready' ? 'none' : 'auto'}>
                            {showTermsAndConditions && (
                                orderDataQuery.loading ? (
                                    <ConditionsSkeleton />
                                ) : orderData?.granularConditionsOfUse ? (
                                    <GranularConditionsBlock
                                        granularConditionsOfUse={orderData.granularConditionsOfUse}
                                        conditionsOfUseUrl={orderData.conditionsOfUseUrl}
                                    />
                                ) : null
                            )}
                            {showTermsAndConditions && (
                                <View style={styles.termsRow}>
                                    <TouchableOpacity
                                        style={[styles.termsCheckbox, termsAccepted && styles.termsCheckboxChecked]}
                                        onPress={() => setTermsAccepted((v) => !v)}
                                        activeOpacity={0.7}>
                                        {termsAccepted && (
                                            <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                                                <path
                                                    d="M1 4L4.5 7.5L11 1"
                                                    stroke="white"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        )}
                                    </TouchableOpacity>
                                    <Text style={styles.termsText}>
                                        {texts.termsConfirmation}
                                        <Text
                                            style={styles.termsLink}
                                            onPress={() => window.open(texts.termsUrl, '_blank')}>
                                            {texts.termsLink}
                                        </Text>
                                    </Text>
                                </View>
                            )}
                            <View style={styles.payButtonWrapper}>
                                {selectedMethod === 'payment_card' ? (
                                    <TouchableOpacity
                                        style={[styles.validateButton, !canPay && styles.validateButtonDisabled]}
                                        onPress={() => checkoutComponentRef.current?.submit()}
                                        disabled={!canPay}
                                        activeOpacity={0.8}>
                                        <Text
                                            style={[
                                                styles.validateButtonText,
                                                !canPay && styles.validateButtonTextDisabled,
                                            ]}>
                                            {texts.validatePayment}
                                        </Text>
                                    </TouchableOpacity>
                                ) : (
                                    /* SDK wallet button — checkout.com renders directly into this container */
                                    <div style={{ position: 'relative', width: '100%' }}>
                                        <div id="checkout-flow-container" style={!canPay ? { opacity: 0.4 } : undefined} />
                                        {!canPay && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    cursor: 'not-allowed',
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                            </View>
                        </View>
                    </View>

                    <View style={styles.rightColumn}>
                        {orderDataQuery.loading ? (
                            <OrderDataSkeleton />
                        ) : orderData ? (
                            <>
                                <GiftCardPreview orderData={orderData} numberOfCards={numberOfCards} />
                                <OrderSummary orderData={orderData} />
                            </>
                        ) : null}
                    </View>
                </View>
            </View>
        </View>
    );
}

export default GiftCardPaymentPage;

const styles = StyleSheet.create({
    page: {
        minHeight: '100vh',
        backgroundColor: '#f7f8fa',
    },

    header: {
        paddingHorizontal: 40,
        paddingVertical: 19,
        borderBottomWidth: 1,
        borderBottomColor: '#e3e3e3',
        backgroundColor: color.white,
    },
    headerLogo: {
        width: 114,
        height: 55,
        resizeMode: 'contain',
    },

    content: {
        paddingHorizontal: 40,
        paddingTop: 16,
        paddingBottom: 40,
    },

    backLink: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 32,
    },
    backIcon: {
        width: 16,
        height: 16,
        resizeMode: 'contain',
    },
    backText: {
        fontFamily: font.instrumentSansSemiBold,
        fontSize: 14,
        lineHeight: 17,
        color: color.black,
        textDecorationLine: 'underline',
    },

    body: {
        flexDirection: 'row',
        gap: 80,
    },

    leftColumn: {
        flex: 1,
    },

    rightColumn: {
        width: 375,
        gap: 16,
    },

    messageContainer: {
        marginBottom: 16,
    },
    pageHeading: {
        fontFamily: font.wallopSansBold,
        fontSize: 24,
        lineHeight: 29,
        letterSpacing: -0.48,
        color: color.black,
    },
    pageSubtitle: {
        fontFamily: font.instrumentSansRegular,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
        marginTop: 4,
    },

    walletMethodsBox: {
        width: '100%',
        borderWidth: 1,
        borderColor: '#E3E3E3',
        borderRadius: 16,
        marginBottom: 8,
        overflow: 'hidden',
    },

    cardFormBox: {
        width: '100%',
        borderWidth: 1,
        borderColor: '#E3E3E3',
        borderRadius: 16,
        padding: 16,
        marginBottom: 8,
    },
    cardFormBoxHidden: {
        opacity: 0,
        pointerEvents: 'none' as const,
        height: 0,
        overflow: 'hidden',
        padding: 0,
        marginBottom: 0,
        borderWidth: 0,
    },

    saveCardRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 16,
        gap: 16,
    },
    saveCardLabel: {
        fontFamily: font.instrumentSansSemiBold,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
    },
    termsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        minHeight: 48,
        paddingVertical: 8,
    },
    termsCheckbox: {
        width: 24,
        height: 24,
        borderRadius: 4,
        borderWidth: 1.5,
        borderColor: '#C4C4C4',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    termsCheckboxChecked: {
        backgroundColor: color.black,
        borderColor: color.black,
    },
    termsText: {
        fontFamily: font.instrumentSansSemiBold,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
        flex: 1,
    },
    termsLink: {
        textDecorationLine: 'underline',
    },
    payButtonWrapper: {
        position: 'relative',
        width: '100%',
    },
    validateButton: {
        backgroundColor: color.black,
        borderRadius: 96,
        height: 44,
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    },
    validateButtonDisabled: {
        backgroundColor: '#c8c8c8',
    },
    validateButtonText: {
        fontFamily: font.instrumentSansSemiBold,
        fontSize: 16,
        lineHeight: 19,
        color: color.white,
        textAlign: 'center',
    },
    validateButtonTextDisabled: {
        color: '#f1f1f1',
    },
});

function ConditionsSkeleton() {
    return (
        <div style={{ width: '100%', border: '1px solid #000', borderRadius: 16, marginBottom: 8, overflow: 'hidden', padding: '16px 16px 0' }}>
            <div className="skeleton-box" style={{ height: 24, width: '55%', marginBottom: 16 }} />
            {[70, 85, 60].map((width, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', paddingBottom: 12, borderBottom: i < 2 ? '1px solid #E3E3E3' : 'none', marginBottom: 12 }}>
                    <div className="skeleton-box" style={{ width: 20, height: 20, flexShrink: 0 }} />
                    <div className="skeleton-box" style={{ height: 16, width: `${width}%` }} />
                </div>
            ))}
        </div>
    );
}

function OrderDataSkeleton() {
    return (
        <>
            <style>{`
                @keyframes shimmer {
                    0% { background-position: -600px 0; }
                    100% { background-position: 600px 0; }
                }
                .skeleton-box {
                    background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
                    background-size: 600px 100%;
                    animation: shimmer 1.4s infinite linear;
                    border-radius: 12px;
                }
            `}</style>
            {/* Card preview skeleton */}
            <div className="skeleton-box" style={{ width: '100%', height: 200, borderRadius: 16 }} />
            {/* Order summary skeleton */}
            <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="skeleton-box" style={{ height: 20, width: '40%' }} />
                <div className="skeleton-box" style={{ height: 16, width: '70%' }} />
                <div className="skeleton-box" style={{ height: 16, width: '55%' }} />
                <div style={{ borderTopWidth: 1, borderTopColor: '#e3e3e3', borderTopStyle: 'solid', paddingTop: 12 }}>
                    <div className="skeleton-box" style={{ height: 20, width: '50%' }} />
                </div>
            </div>
        </>
    );
}
